import { useCallback, useLayoutEffect, useRef } from "preact/hooks";

function normalizeOption(option) {
  return String(option ?? "").trim();
}

function findAutocompleteMatch(options, query) {
  const rawQuery = String(query ?? "");
  if (!rawQuery || rawQuery !== rawQuery.trim()) {
    return null;
  }

  const normalizedQuery = rawQuery;

  if (!normalizedQuery) {
    return null;
  }

  const matches = options.filter((option) => option.toLocaleLowerCase().startsWith(normalizedQuery.toLocaleLowerCase()));

  if (matches.length !== 1) {
    return null;
  }

  return matches[0];
}

export function DataGridAutocompleteEditor({
  column,
  inputRef,
  onChange,
  onBlurCommit,
  value
}) {
  const pendingSelectionRef = useRef(null);
  const options = (column.editor?.options ?? []).map(normalizeOption).filter(Boolean);

  const applyPendingSelection = useCallback(() => {
    const selection = pendingSelectionRef.current;
    const input = inputRef.current;

    if (!selection || !input) {
      return false;
    }

    pendingSelectionRef.current = null;
    input.setSelectionRange(selection.start, selection.end);
    return true;
  }, [inputRef]);

  useLayoutEffect(() => {
    applyPendingSelection();
  }, [applyPendingSelection, value]);

  return (
    <input
      ref={inputRef}
      type={column.inputType ?? "text"}
      step={column.step}
      min={column.min}
      max={column.max}
      value={value}
      onInput={(event) => {
        const input = event.currentTarget;
        const typedValue = event.currentTarget.value;
        const match = findAutocompleteMatch(options, typedValue);

        if (!match || match.length === typedValue.length) {
          pendingSelectionRef.current = null;
          onChange(typedValue);
          return;
        }

        pendingSelectionRef.current = {
          start: typedValue.length,
          end: match.length
        };
        input.value = match;
        applyPendingSelection();
        onChange(match);
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (column.inputType === "date") {
          event.currentTarget.showPicker?.();
        }
      }}
      onKeyDown={(event) => {
        const input = event.currentTarget;
        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? 0;
        const hasSelectedSuffix = selectionStart < selectionEnd && selectionEnd === input.value.length;

        if (!hasSelectedSuffix) {
          return;
        }

        if (event.key === "Backspace") {
          event.preventDefault();
          const nextValue = input.value.slice(0, Math.max(0, selectionStart - 1));
          const match = findAutocompleteMatch(options, nextValue);

          if (match && match.length > nextValue.length) {
            pendingSelectionRef.current = {
              start: nextValue.length,
              end: match.length
            };
            input.value = match;
            applyPendingSelection();
            onChange(match);
            return;
          }

          pendingSelectionRef.current = null;
          onChange(nextValue);
          queueMicrotask(() => {
            input.setSelectionRange(nextValue.length, nextValue.length);
          });
          return;
        }

        if (event.key === "Delete") {
          event.preventDefault();
          const nextValue = input.value.slice(0, selectionStart);
          pendingSelectionRef.current = null;
          onChange(nextValue);
          queueMicrotask(() => {
            input.setSelectionRange(nextValue.length, nextValue.length);
          });
        }
      }}
      onBlur={() => onBlurCommit?.()}
    />
  );
}
